import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useSelector } from 'react-redux';
import { selectClient } from '../model';
import { builtEnvironment } from '@shared/builtEnvironment';
import { isRunningLocal } from '@shared/isRunningEnvironment';

interface UseNavigateToIntakeFormResult {
  navigateToIntakeForm: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export function useNavigateToIntakeForm(): UseNavigateToIntakeFormResult {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { clientId: routeClientId } = useParams<{ clientId?: string }>();
  const client = useSelector(selectClient);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navigateToIntakeForm = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const currentClientId = routeClientId ?? client?.clientId;

      if (!currentClientId) {
        const errorMessage = 'Client ID not found. Please try again.';
        setError(errorMessage);
        alert(errorMessage);
        return;
      }

      const apiBase = `/${isRunningLocal() ? 'dev' : builtEnvironment}/api`;

      await fetch(`${apiBase}/reopen-form/${currentClientId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const response = await fetch(
        `${apiBase}/client/${currentClientId}/form-reference`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = 'Failed to load intake form. Please try again.';
        setError(errorMessage);
        alert(errorMessage);
        return;
      }

      const { formReferenceId } = await response.json();
      navigate(`/onboarding-form/${formReferenceId}`);
    } catch (err) {
      const errorMessage =
        'An error occurred while loading the intake form. Please try again.';
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently, routeClientId, client?.clientId, navigate]);

  return {
    navigateToIntakeForm,
    isLoading,
    error,
  };
}

